import { X } from "@phosphor-icons/react";
import { fmtClock } from "@/lib/format";

// The shape of the conversation, inferred after each chapter. Order and wording match the backend's board kinds.
export const BOARD_KINDS = [
  ["anchor", "Anchor", "What this conversation is trying to decide or achieve"],
  ["fact", "Facts", "Stated as known"],
  ["assumption", "Assumptions", "Treated as true, not established"],
  ["constraint", "Constraints", "Limits to work within"],
  ["option", "Options", "Proposed ways forward"],
  ["agreed", "Agreed", "Settled by the room"],
  ["open", "Open", "Disagreements and unanswered questions"],
];

const CHEAP = /^\(cheap\)\s*/i;

const Item = ({ item, onRemove }) => {
  const cheap = item.kind === "option" && CHEAP.test(item.text);
  return (
    <li className="group flex items-start gap-3 py-2 border-b border-[var(--o-border-soft)] last:border-b-0" data-testid="board-item">
      <div className="flex-1 min-w-0">
        <p className="text-sm leading-relaxed text-[var(--o-fg2)]">
          {cheap && <span className="font-mono text-[10px] uppercase tracking-[0.1em] px-1 py-px mr-1.5 border border-[#84CC16]/50 text-[#84CC16]">cheap</span>}
          {item.text.replace(CHEAP, "")}
        </p>
        <p className="font-mono text-[10px] text-[var(--o-muted3)] mt-0.5">{fmtClock(item.t)}{item.who ? ` · ${item.who}` : ""}</p>
      </div>
      {onRemove && (
        <button onClick={() => onRemove(item)} className="p-1.5 -m-1 text-[var(--o-muted3)] hover:text-[var(--o-fg)] sm:opacity-0 sm:group-hover:opacity-100 focus:opacity-100 transition-opacity" title="Wrong? Remove it. The board won't add it back." aria-label="Remove from the board" data-testid="board-remove">
          <X size={13} />
        </button>
      )}
    </li>
  );
};

export const BoardSections = ({ items, onRemove, compact = false }) => (
  <div className={compact ? "space-y-5" : "grid md:grid-cols-2 gap-x-8 gap-y-6"}>
    {BOARD_KINDS.map(([kind, title, hint]) => {
      const rows = items.filter((i) => i.kind === kind);
      if (!rows.length && compact) return null;
      return (
        <section key={kind} className={kind === "anchor" && !compact ? "md:col-span-2" : ""} data-testid={`board-${kind}`}>
          <div className="flex items-baseline gap-2 border-b border-[var(--o-border)] pb-1.5">
            <h3 className="font-display text-sm font-bold">{title}</h3>
            <span className="text-[11px] text-[var(--o-muted3)] truncate">{hint}</span>
            <span className="ml-auto font-mono text-[10px] text-[var(--o-muted3)]">{rows.length || ""}</span>
          </div>
          {rows.length ? (
            <ul>{rows.map((i) => <Item key={i.id} item={i} onRemove={onRemove} />)}</ul>
          ) : (
            <p className="text-xs text-[var(--o-muted3)] py-2">Nothing yet</p>
          )}
        </section>
      );
    })}
  </div>
);

export const Board = ({ items = [], through = 0, onRemove }) => (
  <div className="thin-scroll flex-1 min-h-0 overflow-y-auto px-4 sm:px-8 py-5" data-testid="board">
    <p className="text-xs text-[var(--o-muted2)] mb-5 max-w-2xl">
      Inferred by the AI after each chapter{through > 0 ? `, up to ${fmtClock(through)}` : ""}. The Observer reasons against it. Remove anything wrong with ✕ and it won&rsquo;t come back.
    </p>
    {items.length === 0 ? (
      <p className="text-sm text-[var(--o-muted3)]" data-testid="board-empty">The board fills in after the first chapter: anchor, facts, assumptions, constraints, options, what was agreed, and what is still open.</p>
    ) : (
      <BoardSections items={items} onRemove={onRemove} />
    )}
  </div>
);
